import React, { useState, useEffect } from "react";
import {
    Package, Clock, ShoppingCart, CheckCircle2,
    Search, ExternalLink, RefreshCw, User, X, Calendar, Hash
} from "lucide-react";
import api from "../api/axiosInstance";

const AdminMaterialRequests = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("All");
    const [searchTerm, setSearchTerm] = useState("");

    // New States for Detailed View Modal
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);

    useEffect(() => {
        fetchRequests();
    }, []);

    const fetchRequests = async () => {
        setLoading(true);
        try {
            const res = await api.get("get_all_material_requests.php");
            setRequests(res.data.requests || []);
        } catch (err) {
            console.error("Fetch failed:", err);
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (requestId, newStatus) => {
        try {
            const res = await api.post("update_request_status.php", {
                request_id: requestId,
                status: newStatus
            });
            if (res.data.status === "success") {
                // If modal is open, sync the state inside the modal too
                if (selectedRequest && selectedRequest.id === requestId) {
                    setSelectedRequest({ ...selectedRequest, status: newStatus });
                }
                fetchRequests();
            }
        } catch (err) {
            alert("Status update failed");
        }
    };

    const filteredData = requests.filter(req => {
        const matchesFilter = filter === "All" || req.status === filter;
        const matchesSearch = req.item_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            req.project_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            req.supervisor_name.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesFilter && matchesSearch;
    });

    if (loading) return <div className="p-20 text-center font-black animate-pulse text-slate-400 uppercase tracking-widest">Loading Logistics...</div>;

    return (
        <div className="p-8 space-y-8 bg-slate-50/50 min-h-screen relative">
            {/* Header */}
            <div className="flex justify-between items-end">
                <div>
                    <h1 className="text-4xl font-black italic tracking-tighter uppercase text-slate-900">Procurement Hub</h1>
                    <p className="text-[10px] font-black uppercase text-indigo-500 tracking-widest mt-1">Material Request Management</p>
                </div>
                <button onClick={fetchRequests} className="p-3 bg-white border border-slate-100 rounded-2xl shadow-sm hover:rotate-180 transition-all duration-500">
                    <RefreshCw size={20} className="text-slate-400" />
                </button>
            </div>

            {/* Filters & Search */}
            <div className="flex flex-wrap gap-4 items-center justify-between">
                <div className="flex bg-white p-1.5 rounded-2xl border border-slate-100 shadow-sm">
                    {["All", "Pending", "Ordered", "Delivered"].map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setFilter(tab)}
                            className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${filter === tab ? "bg-slate-900 text-white shadow-lg" : "text-slate-400 hover:text-slate-600"
                                }`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                        type="text"
                        placeholder="Search by item, supervisor or site name..."
                        className="w-full bg-white border border-slate-100 rounded-2xl py-4 pl-12 pr-4 text-sm font-bold shadow-sm outline-none focus:ring-4 ring-indigo-500/5"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Requests Table */}
            <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm overflow-hidden">
                <table className="w-full text-left">
                    <thead className="bg-slate-50 border-b border-slate-100">
                        <tr>
                            <th className="p-6 text-[10px] font-black uppercase text-slate-400 tracking-widest">Site / Project</th>
                            <th className="p-6 text-[10px] font-black uppercase text-slate-400 tracking-widest">Requested By</th>
                            <th className="p-6 text-[10px] font-black uppercase text-slate-400 tracking-widest">Material & Qty</th>
                            <th className="p-6 text-[10px] font-black uppercase text-slate-400 tracking-widest">Date</th>
                            <th className="p-6 text-[10px] font-black uppercase text-slate-400 tracking-widest text-center">Status</th>
                            <th className="p-6 text-[10px] font-black uppercase text-slate-400 tracking-widest text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {filteredData.map((req) => (
                            <tr key={req.id} className="hover:bg-slate-50/50 transition-all">
                                <td className="p-6">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center text-indigo-600 font-black uppercase">
                                            {req.project_name ? req.project_name[0] : 'P'}
                                        </div>
                                        <div>
                                            <p className="font-black text-slate-900 uppercase italic text-sm">{req.project_name}</p>
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">Site ID: #{req.project_id}</p>
                                        </div>
                                    </div>
                                </td>
                                <td className="p-6">
                                    <div className="flex items-center gap-2">
                                        <div className="p-2 bg-slate-100 rounded-lg">
                                            <User size={14} className="text-slate-500" />
                                        </div>
                                        <div>
                                            <p className="font-black text-slate-800 text-[11px] uppercase">{req.supervisor_name}</p>
                                            <p className="text-[9px] font-bold text-indigo-500">ID: #{req.supervisor_id}</p>
                                        </div>
                                    </div>
                                </td>
                                <td className="p-6">
                                    <p className="font-black text-slate-800 text-sm">{req.item_name}</p>
                                    <p className="text-[11px] font-bold text-indigo-600 uppercase tracking-widest">{req.qty}</p>
                                </td>
                                <td className="p-6 text-[11px] font-black text-slate-500 uppercase">{req.date}</td>
                                <td className="p-6 text-center">
                                    <span className={`px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest ${req.status === 'Pending' ? 'bg-amber-100 text-amber-600' :
                                        req.status === 'Ordered' ? 'bg-blue-100 text-blue-600' : 'bg-emerald-100 text-emerald-600'
                                        }`}>
                                        {req.status}
                                    </span>
                                </td>
                                <td className="p-6 text-right">
                                    <div className="flex justify-end gap-2">
                                        {req.status === 'Pending' && (
                                            <button
                                                onClick={() => updateStatus(req.id, 'Ordered')}
                                                className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-600 hover:text-white transition-all"
                                                title="Mark as Ordered"
                                            >
                                                <ShoppingCart size={16} />
                                            </button>
                                        )}
                                        {req.status === 'Ordered' && (
                                            <button
                                                onClick={() => updateStatus(req.id, 'Delivered')}
                                                className="p-2.5 bg-emerald-50 text-emerald-600 rounded-xl hover:bg-emerald-600 hover:text-white transition-all"
                                                title="Mark as Delivered"
                                            >
                                                <CheckCircle2 size={16} />
                                            </button>
                                        )}
                                        {/* LINKED EXTERNAL LINK BUTTON TO OPEN DETAILED MODAL OVERLAY */}
                                        <button
                                            onClick={() => {
                                                setSelectedRequest(req);
                                                setDetailModalOpen(true);
                                            }}
                                            className="p-2.5 bg-slate-50 text-slate-400 rounded-xl hover:bg-slate-900 hover:text-white transition-all"
                                        >
                                            <ExternalLink size={16} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* --- DYNAMIC LOGISTICS DETAIL MODAL OVERLAY --- */}
            {detailModalOpen && selectedRequest && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-[40px] shadow-2xl border border-slate-100 w-full max-w-xl p-8 animate-in zoom-in-95 duration-200">
                        {/* Modal Header */}
                        <div className="flex justify-between items-center pb-4 border-b border-slate-50">
                            <div>
                                <h3 className="text-xl font-black italic uppercase tracking-tighter text-slate-900">Requisition Audit View</h3>
                                <p className="text-[10px] font-black uppercase text-indigo-500 tracking-widest mt-0.5">Request Reference Key: #{selectedRequest.id}</p>
                            </div>
                            <button
                                onClick={() => {
                                    setDetailModalOpen(false);
                                    setSelectedRequest(null);
                                }}
                                className="p-2 hover:bg-slate-100 rounded-full transition-all"
                            >
                                <X size={20} className="text-slate-400" />
                            </button>
                        </div>

                        {/* Modal Metrics Grid */}
                        <div className="grid grid-cols-2 gap-6 my-6">
                            <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100/50 space-y-1">
                                <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider flex items-center gap-1">
                                    <Hash size={12} /> Target Project Horizon
                                </span>
                                <p className="font-black text-slate-800 text-sm uppercase">{selectedRequest.project_name}</p>
                                <p className="text-[10px] font-bold text-slate-400 uppercase">ID: #{selectedRequest.project_id}</p>
                            </div>

                            <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100/50 space-y-1">
                                <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider flex items-center gap-1">
                                    <User size={12} /> Submitting Field Agent
                                </span>
                                <p className="font-black text-slate-800 text-sm uppercase">{selectedRequest.supervisor_name}</p>
                                <p className="text-[10px] font-bold text-slate-400 uppercase">Supervisor ID: #{selectedRequest.supervisor_id}</p>
                            </div>

                            <div className="col-span-2 bg-slate-900 text-white p-6 rounded-3xl space-y-2">
                                <span className="text-[9px] font-black text-indigo-300 uppercase tracking-widest flex items-center gap-1">
                                    <Package size={14} /> Supply Component & Volume Specification
                                </span>
                                <p className="text-lg font-black tracking-tight">{selectedRequest.item_name}</p>
                                <div className="flex justify-between items-center pt-2 border-t border-white/10">
                                    <span className="text-xs font-black uppercase text-emerald-400 bg-emerald-500/10 px-3 py-1 rounded-lg">Allocation: {selectedRequest.qty}</span>
                                    <span className="text-[10px] font-bold text-slate-400 flex items-center gap-1"><Calendar size={12} /> Registered: {selectedRequest.date}</span>
                                </div>
                            </div>
                        </div>

                        {/* Workflow Control Component Section */}
                        <div className="space-y-3 pt-2">
                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block ml-1">Advance Operational State Lifecycle</label>
                            <div className="grid grid-cols-3 gap-3">
                                <button
                                    onClick={() => updateStatus(selectedRequest.id, 'Pending')}
                                    className={`py-3.5 rounded-2xl text-[10px] font-black uppercase tracking-wider transition-all border ${selectedRequest.status === 'Pending'
                                        ? 'bg-amber-100 border-amber-200 text-amber-700 font-extrabold shadow-inner'
                                        : 'bg-white border-slate-200 text-slate-400 hover:bg-slate-50'
                                        }`}
                                >
                                    Pending Review
                                </button>
                                <button
                                    onClick={() => updateStatus(selectedRequest.id, 'Ordered')}
                                    className={`py-3.5 rounded-2xl text-[10px] font-black uppercase tracking-wider transition-all border ${selectedRequest.status === 'Ordered'
                                        ? 'bg-blue-100 border-blue-200 text-blue-700 font-extrabold shadow-inner'
                                        : 'bg-white border-slate-200 text-slate-400 hover:bg-slate-50'
                                        }`}
                                >
                                    Mark Ordered
                                </button>
                                <button
                                    onClick={() => updateStatus(selectedRequest.id, 'Delivered')}
                                    className={`py-3.5 rounded-2xl text-[10px] font-black uppercase tracking-wider transition-all border ${selectedRequest.status === 'Delivered'
                                        ? 'bg-emerald-100 border-emerald-200 text-emerald-700 font-extrabold shadow-inner'
                                        : 'bg-white border-slate-200 text-slate-400 hover:bg-slate-50'
                                        }`}
                                >
                                    Mark Delivered
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminMaterialRequests;